import autosize from 'autosize';
import PropTypes from 'prop-types';
import React from 'react';
import OpenDownload from 'components/Questions/UploadResponseQuestion/OpenDownload';
import FeatureFlag from 'components/shared/FeatureFlag';
import PreserveFormatting from 'components/shared/PreserveFormatting';
import CriteriaList from 'components/VideoResponse/Grading/CriteriaList';
import GradingStationVideoResponse from 'components/VideoResponse/Grading/GradingStationVideoResponse';
import { routes } from 'constants/config';
import { Icon } from 'ui';
import ConditionalList from 'components/shared/ConditionalList';
import QuestionDetails from '../../shared/QuestionDetails';
import GraderNotesSection from './GraderNotesSection';
import GradingSection from './GradingSection';

const answerAsArray = answer => {
  if (Array.isArray(answer)) {
    return [...answer];
  }
  return [answer];
};

class ToGradeRow extends React.PureComponent {
  state = {
    correct: this.props.answer.correct,
    feedback: this.props.answer.feedback,
    criteriaRatings: this.props.answer.criteriaRatings,
  };

  textarea = React.createRef();

  setAnswerCorrect = answerCorrect => {
    this.setState(
      {
        correct: answerCorrect,
      },
      () => {
        const { setGradedAnswer } = this.props;
        setGradedAnswer(
          answerCorrect,
          this.state.feedback,
          this.state.criteriaRatings,
        );
      },
    );
  };

  setAnswerFeedback = answerFeedback => {
    this.setState(
      {
        feedback: answerFeedback,
      },
      () => {
        const { setGradedAnswer } = this.props;
        setGradedAnswer(
          this.state.correct,
          answerFeedback,
          this.state.criteriaRatings,
        );
        this.autosizeUpdate();
      },
    );
  };

  setGradingRating = (answerId, criteriaId, score) => {
    // TODO: connect this to global state once available, remove pubsub
    const { answer } = this.props;
    const { criteriaRatings } = this.state;
    if (answerId === answer.id) {
      const newCriteriaRatings = criteriaRatings.map(criteriaRating => {
        if (criteriaRating.id === criteriaId) {
          return {
            ...criteriaRating,
            score,
          };
        }
        return criteriaRating;
      });
      this.setState(
        {
          criteriaRatings: newCriteriaRatings,
        },
        () => {
          const { setGradedAnswer } = this.props;
          setGradedAnswer(
            this.state.correct,
            this.state.feedback,
            newCriteriaRatings,
          );
        },
      );
    }
  };

  componentDidMount() {
    this.autosizeTextArea();
    global.pubsub.subscribe('grading:rating', this.setGradingRating);
  }

  componentWillUnmount() {
    global.pubsub.unsubscribe('grading:rating', this.setGradingRating);
  }

  autosizeTextArea = () => {
    if (this.textarea.current) {
      autosize(this.textarea.current);
    }
  };

  autosizeUpdate = () => {
    if (this.textarea.current) {
      autosize.update(this.textarea.current);
    }
  };

  allCommentsAreInactive = () => {
    return this.props.answer?.questionAssociatedComments?.every(
      comment => !comment.active,
    );
  };

  render() {
    const { answer, currentUser, currentUserId, ziggeoApiKey, isAdmin } =
      this.props;
    const { criteriaRatings } = this.state;
    return (
      <>
        <div className="d-f w-fill border border-gray-light border-top-0 px-8 py-16">
          <span className="w-25p ml-24 pr-8 text-wrap">
            {answer.questionText}
            {!answer.isChat &&
              answer.questionMetadataPresent &&
              answer.questionMetadata && (
                <>
                  <div className="fw-b mt-8 mb-8">
                    {t(
                      `questions.question.${answer.questionType}.display_header`,
                    )}
                  </div>
                  <QuestionDetails details={answer.questionMetadata} />
                </>
              )}
            {answer.questionMetadataPresent &&
              answer.questionMetadata &&
              answer.isChat && (
                <>
                  <div className="fw-b mt-8">
                    {t('questions.question.chat.display_header')}:
                  </div>
                  <PreserveFormatting preferParagraphs>
                    {answer.questionMetadata.message}
                  </PreserveFormatting>
                </>
              )}
          </span>
          <span className="w-25p pr-8 text-wrap">
            {answer.isUploadResponse && answer.givenAnswer && (
              <OpenDownload
                answerId={answer.id}
                fileName={answer.givenAnswer}
              />
            )}
            {answer.isMediaResponse && answer.givenAnswer && (
              <GradingStationVideoResponse
                answer={answer}
                ziggeoApiKey={ziggeoApiKey}
                currentUser={currentUser}
                currentUserId={currentUserId}
                answerCorrect={answer.correct}
                setAnswerCorrect={this.setAnswerCorrect}
                answerFeedback={answer.feedback}
                setAnswerFeedback={this.setAnswerFeedback}
                criteriaRatings={criteriaRatings}
                isAdmin={isAdmin}
              />
            )}
            {!answer.isChat &&
              !answer.isMediaResponse &&
              answer.hasAnswerWithMetadata && (
                <QuestionDetails details={answer.givenAnswer} />
              )}
            {!answer.isMediaResponse &&
              !answer.hasAnswerWithMetadata &&
              !answer.isUploadResponse &&
              answer.givenAnswer && (
                <ConditionalList list={answerAsArray(answer.givenAnswer)} />
              )}
            {answer.isChat &&
              answer.hasAnswerWithMetadata &&
              answer.givenAnswer && (
                <>
                  <span className="fw-b mt-8 d-ib">
                    {t('questions.question.chat.display_header')}:
                  </span>
                  {answer.givenAnswer}
                </>
              )}
            {!answer.givenAnswer && t('progress.report_card.no_answer')}
          </span>
          {answer?.questionAssociatedComments?.length &&
          !this.allCommentsAreInactive() ? (
            <span className="w-20p d-f fd-c pr-8">
              <GraderNotesSection
                comments={answer.questionAssociatedComments}
              />
            </span>
          ) : (
            <span className="w-20p d-f fd-c pr-8">{t('grades.ungraded')}</span>
          )}

          <div className="fg-1">
            <div className="d-f fd-c mb-8">
              <GradingSection
                id={answer.id}
                correct={this.state.correct}
                setAnswerCorrect={this.setAnswerCorrect}
              />
            </div>
            <div className="mb-24">
              <div className="w-fill pr-16">
                <textarea
                  ref={this.textarea}
                  className="Textarea bg-white p-8 bdrs-2 h-70 w-fill"
                  id={`answer${answer.id}_feedback`}
                  placeholder={t('grades.feedback.provide_feedback')}
                  onChange={e => this.setAnswerFeedback(e.target.value)}
                  value={this.state.feedback || ''}
                />
              </div>
              {answer.canViewAnswerFeedback && (
                <FeatureFlag features={['practice']}>
                  <div className="c-brand-blue mt-4">
                    <a
                      target="_blank"
                      rel="noreferrer noopener"
                      href={routes.QUESTIONS.FEEDBACK(answer.questionId)}
                    >
                      {t('progress.previous_comments')}{' '}
                      <Icon name="external-link" className="ml-4 mt-0" />
                    </a>
                  </div>
                </FeatureFlag>
              )}
            </div>
            {answer.criteriaRatings && answer.criteriaRatings.length > 0 && (
              <FeatureFlag features={['practice']}>
                <div>
                  <CriteriaList
                    data={{
                      id: answer.id,
                      criteriaRatings: answer.criteriaRatings,
                      ratingOptions: answer.ratingOptions,
                    }}
                  />
                </div>
              </FeatureFlag>
            )}
          </div>
        </div>
      </>
    );
  }
}

ToGradeRow.propTypes = {
  answer: PropTypes.shape({
    position: PropTypes.number,
    questionText: PropTypes.string,
    givenAnswer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    questionMetadata: PropTypes.shape({ message: PropTypes.string }),
    isMediaResponse: PropTypes.bool,
    hasAnswerWithMetadata: PropTypes.bool,
    questionType: PropTypes.string,
    hasQuestionMetadata: PropTypes.bool,
    questionMetadataPresent: PropTypes.bool,
    isChat: PropTypes.bool,
    isGraded: PropTypes.bool,
    questionId: PropTypes.number,
    gradable: PropTypes.bool,
    givenAnswerIsIlos: PropTypes.bool,
    correct: PropTypes.string,
    feedback: PropTypes.string,
    criteriaRatings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        criterion_id: PropTypes.number,
        name: PropTypes.string,
        description: PropTypes.string,
        score: PropTypes.number,
      }),
    ),
    ratingOptions: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        score: PropTypes.number,
      }),
    ),
    mediaShareLink: PropTypes.string,
    hasBeenSkipped: PropTypes.bool,
    canViewAnswerFeedback: PropTypes.bool,
    id: PropTypes.number,
    userName: PropTypes.string,
    lessonName: PropTypes.string,
    daysAwaitingGrade: PropTypes.number,
    isUploadResponse: PropTypes.bool,
  }),
  currentUser: PropTypes.string,
  setGradedAnswer: PropTypes.func,
  ziggeoApiKey: PropTypes.string,
};

ToGradeRow.defaultProps = {
  answer: {},
  currentUser: 'user',
  setGradedAnswer: () => {},
  ziggeoApiKey: '',
};

export default ToGradeRow;
